import PaidIcon from '@mui/icons-material/Paid';
import { alpha, styled } from '@mui/material/styles';

import React, { useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { NotFoundError } from '_lib/api/apiErrors';
import { isStCapacityCompany, SisterCompany, TradeEnum } from '_lib/api/apiTypes.shim';
import { Box } from '_lib/components/Box/Box';
import { Button, ButtonProps } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { ExternalLink } from '_lib/components/Link/Link';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyLabels } from '_lib/constants/company.constants';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { HeaderTextBlock } from '_lib/featureComponents/HeaderTextBlock/HeaderTextBlock';
import { TextBlockTypography } from '_lib/featureComponents/TextBoxTypography/TextBoxTypography';
import { TradeIcon, TradesDisplay } from '_lib/featureComponents/Trades/TradesDisplay';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { canOrCant, formatAddressTwoLine, formatPhone, getFullName } from '_lib/utils/helpers';
import { reportException } from '_lib/utils/sentryService';

import { CompanyContext, ZoneZipContext } from '../Company.slice';
import {
  getBoxStyleForCanIncludeFee,
  getBoxStyleForCanWaiveFee,
  getLeftHighlightOkSx,
  getLeftHighlightWarnSx
} from '../CompanyPane.styles';
import { CompanyScheduleUrlHeaderBox } from '../CompanyScheduleUrl/CompanyScheduleUrl';
import { ZoneOverrideTextBlock } from '../ZoneOverrideUtils/ZoneOverrideUtils';

interface CompanySummaryProps {
  onCompanySwitchConfirmed: (companyId: string) => void;
  companyContext: CompanyContext;
  zoneZipContext: ZoneZipContext | undefined;
  phoneCallURLState: Pick<PhoneCallURLState, 'companyDNIS'>;
  showScheduleUrlAnimation?: boolean;
  disableGreeting?: boolean;
}

const StyledSisterCompanyDivider = styled(Divider)(({ theme }) => ({
  borderWidth: 1,
  borderColor: alpha(theme.palette.common.white, 0.18)
}));

interface SisterCompanyButtonProps extends Omit<ButtonProps, 'onClick'> {
  onClick: (id: string) => void;
  sisterCompany: SisterCompany;
  /**
   * @default 'contained'
   */
  variant?: ButtonProps['variant'];
}

const SisterCompanyButton = (props: SisterCompanyButtonProps) => {
  const { onClick, sisterCompany, variant, ...rest } = props;
  return (
    <Button onClick={() => onClick(sisterCompany.id)} variant={variant || 'contained'} {...rest}>
      <Stack sx={{ textTransform: 'uppercase', width: '100%' }}>
        {sisterCompany.name}
        <StyledSisterCompanyDivider />
        {sisterCompany.callCenter.trades.map((trade) => TradeEnum[trade]).join(', ')}
      </Stack>
    </Button>
  );
};

export const CompanySummary = (props: CompanySummaryProps): React.ReactElement => {
  const {
    onCompanySwitchConfirmed,
    companyContext,
    zoneZipContext,
    phoneCallURLState,
    showScheduleUrlAnimation,
    disableGreeting
  } = props;
  const { company } = companyContext;

  const confirmationDialog = useConfirmationDialog();

  const api = useContext(ApiContext);
  const userDetailQuery = api.users.useGetById({
    id: company.modifiedById,
    extraOptions: { disableSnackOnNotFoundError: true },
    disableErrorReporting: true
  });
  const { data: appConfigData } = api.appConfig.useCachedAppConfigQuery();
  const companyConfigItem = appConfigData?.companyConfigItems?.find((o) => o.companyId === company.id);
  const companyAgreementItem = appConfigData?.companyAgreements?.find((o) => o.companyId === company.id);

  // It's possible that a 404 could be returned if the last user that updated a company was a System User.
  // So we'll report any other error
  if (userDetailQuery.error && !(userDetailQuery.error instanceof NotFoundError)) {
    reportException(userDetailQuery.error, {
      extra: { userId: company.modifiedById, companyId: company.id, companyName: company.name }
    });
  }

  const handleCompanySwitchClicked = async (sisterCompanyId: string) => {
    const sisterCompany = company.sisterCompanies?.find((sisterCompany) => sisterCompany.id === sisterCompanyId);
    if (sisterCompany) {
      const promise = await confirmationDialog.show({
        titleText: 'CONFIRM COMPANY SWITCH',
        contentText: `Do you want to switch the current company to ${sisterCompany.name}?`
      });
      if (promise?.action === 'CONFIRM') {
        onCompanySwitchConfirmed(sisterCompany.id);
      }
    }
  };

  const greeting =
    company.callCenter.dnisArray.find((dnis) => dnis.phoneNumber === phoneCallURLState.companyDNIS)?.greetingOverride ||
    company.callCenter.greetingMessage;

  const modifiedAtString = DateUtils.format(company.modifiedAt, DateFormatEnum.DateAtTime);
  const modifiedByText = `Updated ${modifiedAtString} by ${
    userDetailQuery.data ? getFullName(userDetailQuery.data) : 'System User'
  }`;

  const nationalNumberGroup =
    company.callCenter.nationalNumberGroupId &&
    appConfigData?.nationalNumberGroups &&
    appConfigData.nationalNumberGroups.find((nng) => nng.id === company.callCenter.nationalNumberGroupId);

  return (
    // even though it's not consistent, we're going to use a smaller padding-top for more vertical space
    <Box sx={{ px: 3, paddingTop: 1, paddingBottom: 2 }}>
      <Stack useFlexGap>
        {!disableGreeting && (
          <FlexBox
            sx={(theme) => ({
              backgroundColor: theme.palette.secondary.light,
              width: '100%',
              borderRadius: 1,
              px: 2,
              py: 1
            })}>
            <Typography text={greeting} />
          </FlexBox>
        )}
        <FlexBox sx={{ justifyContent: 'center' }}>
          <Typography
            variant='body2'
            sx={{ textTransform: 'uppercase', opacity: 0.7, fontWeight: 700 }}
            text={modifiedByText}
          />
        </FlexBox>
        <FlexBox sx={{ justifyContent: 'center' }}>
          {companyAgreementItem?.agreementAppUrl && (
            <Button
              onClick={() => window.open(companyAgreementItem.agreementAppUrl, '_blank')}
              color='success'
              startIcon={<PaidIcon />}
              variant='contained'>
              Sell New Membership
            </Button>
          )}
        </FlexBox>
        <Stack direction='row' spacing={2} sx={{ justifyContent: 'flex-start', marginTop: 1 }}>
          <Stack spacing={2} useFlexGap sx={{ width: '50%' }}>
            <HeaderTextBlock headerText='Service Type' text={company.callCenter.serviceType} />
            <HeaderBox headerText='Trades'>
              <TradesDisplay trades={company.callCenter.trades} color='primary' />
            </HeaderBox>
            <HeaderTextBlock headerText='Owner/GM' text={company.callCenter.ownerName} />
            <HeaderTextBlock headerText='Address' text={formatAddressTwoLine(company)} />
            <HeaderBox headerText='Sister Companies'>
              <Stack sx={{ marginTop: 1, marginBottom: 2 }}>
                {company.sisterCompanies.length > 0 ? (
                  company.sisterCompanies.map((sisterCompany) => (
                    <Tooltip followCursor key={sisterCompany.id} title={`Switch to ${sisterCompany.name}`}>
                      <SisterCompanyButton
                        fullWidth
                        sisterCompany={sisterCompany}
                        onClick={handleCompanySwitchClicked}
                      />
                    </Tooltip>
                  ))
                ) : (
                  <Typography text='This company does not have sister companies.' />
                )}
              </Stack>
            </HeaderBox>
            {nationalNumberGroup && (
              <HeaderBox headerText='National Numbers'>
                <Stack>
                  {nationalNumberGroup.nationalNumbers.map((nn) => (
                    <Stack key={nn.trade} direction='row'>
                      <TradeIcon trade={nn.trade} color='primary' />
                      <TextBlockTypography key={nn.trade} text={`${formatPhone(nn.phoneNumber)} - ${nn.name}`} />
                    </Stack>
                  ))}
                </Stack>
              </HeaderBox>
            )}
            <HeaderTextBlock headerText='Related Organizations' text={company.callCenter.relatedOrganizations} />
            <HeaderBox headerText='Work/Services NOT to Book'>
              <TextBlockTypography
                sx={(theme) => getLeftHighlightWarnSx(theme)}
                text={company.callCenter.workNotToBookRules}
              />
            </HeaderBox>
            <HeaderBox headerText='Work/Services to Book'>
              <TextBlockTypography
                sx={(theme) => getLeftHighlightOkSx(theme)}
                text={company.callCenter.workToBookRules}
              />
            </HeaderBox>
          </Stack>
          <Stack spacing={2} useFlexGap sx={{ width: '50%' }}>
            {!company.isScheduleEnabled && !isStCapacityCompany(company) && (
              <CompanyScheduleUrlHeaderBox
                companyContext={companyContext}
                zoneZipContext={zoneZipContext}
                showAnimation={showScheduleUrlAnimation}
              />
            )}
            <HeaderTextBlock headerText='Value' text={company.callCenter.additionalNotes} />
            <Typography component='p' sx={(theme) => getBoxStyleForCanIncludeFee(company, theme)}>
              {'Appointment fee '}
              <span style={{ fontWeight: 'bold' }}>{canOrCant(company.callCenter.canIncludeFee)}</span>
              {' be included with work'}
            </Typography>
            <Typography component='p' sx={(theme) => getBoxStyleForCanWaiveFee(company, theme)}>
              {'Appointment fee '}
              <span style={{ fontWeight: 'bold' }}>{canOrCant(company.callCenter.canWaiveFee)}</span>
              {' be waived'}
            </Typography>
            {companyConfigItem?.jobTypeInfoUrl && (
              <HeaderBox headerText='Job Type URL'>
                <ExternalLink href={companyConfigItem.jobTypeInfoUrl}>{CompanyLabels.JOB_TYPE_INFO_URL}</ExternalLink>
              </HeaderBox>
            )}
            <ZoneOverrideTextBlock
              headerText='Dispatch Rules'
              company={company}
              zoneCallCenterProp={'dispatchRules'}
              zone={zoneZipContext?.selectedZone}
            />
            <HeaderTextBlock headerText='Fee Waiver Rules' text={company.callCenter.serviceFeeWaiverRules} />
            <ZoneOverrideTextBlock
              headerText='Big Ticket Items'
              company={company}
              zoneCallCenterProp={'bigTicketItems'}
              zone={zoneZipContext?.selectedZone}
            />
            <ZoneOverrideTextBlock
              headerText='Big Ticket Item Scheduling Rules'
              company={company}
              zoneCallCenterProp={'bigTicketItemRules'}
              zone={zoneZipContext?.selectedZone}
            />
          </Stack>
        </Stack>
      </Stack>
      {confirmationDialog.element}
    </Box>
  );
};
